import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { type GameMeta, isValidSlug } from '@photo-merge/shared';
import { PageHeader } from '../components/PageHeader';
import { Placeholder } from '../components/Placeholder';
import { Sheet } from '../components/Sheet';
import { GameBoard } from '../game/GameBoard';
import { Leaderboard, ScoreSubmit } from '../game/Leaderboard';
import { ScoreStrip } from '../game/ScoreStrip';
import { useScores } from '../game/useScores';
import { loadBallArt, loadStockArt, type BallArt } from '../game/art';
import { ApiError, fetchGame } from '../lib/api';
import styles from './Play.module.css';

/** Until a game is saved, Quick Play uses this many random stock faces. */
const QUICK_PLAY_BALLS = 11;

export function Play({ random = false }: { random?: boolean }) {
  const { slug } = useParams<{ slug: string }>();
  if (random) return <QuickPlay />;
  if (!slug || !isValidSlug(slug)) return <BadLink slug={slug} />;
  return <SavedGame slug={slug} />;
}

function QuickPlay() {
  const [art, setArt] = useState<BallArt | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadStockArt(QUICK_PLAY_BALLS).then((a) => {
      if (!cancelled) setArt(a);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className={styles.page}>
      <PageHeader title="Quick play" subtitle="A random cast of faces" compact />
      {art ? (
        <GameBoard
          ballCount={QUICK_PLAY_BALLS}
          art={art}
          bestKey="random"
          shareUrl={`${window.location.origin}/random`}
        />
      ) : (
        <div className={styles.loading}>Shuffling the deck…</div>
      )}
    </main>
  );
}

function BadLink({ slug }: { slug?: string }) {
  return (
    <main className="page">
      <PageHeader title="Game not found" />
      <Placeholder
        phase="Bad link"
        what={`"${slug ?? ''}" isn't a valid game link`}
        detail="Check the link and try again, or head back and make your own."
      />
    </main>
  );
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; game: GameMeta; art: BallArt }
  | { status: 'missing' }
  | { status: 'error'; message: string };

function SavedGame({ slug }: { slug: string }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [finalScore, setFinalScore] = useState<number | null>(null);
  const [boardOpen, setBoardOpen] = useState(false);
  const scores = useScores(slug);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });

    (async () => {
      try {
        const game = await fetchGame(slug, controller.signal);
        // Images are loaded up front: a ball appearing mid-game looks like a glitch.
        const art = await loadBallArt(game.balls);
        if (!controller.signal.aborted) setState({ status: 'ready', game, art });
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.status === 404) {
          setState({ status: 'missing' });
        } else {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Could not load that game.',
          });
        }
      }
    })();

    return () => controller.abort();
  }, [slug]);

  const handleGameOver = useCallback((score: number) => setFinalScore(score), []);
  const handleRestart = useCallback(() => {
    setFinalScore(null);
    scores.reset();
  }, [scores]);

  if (state.status === 'loading') {
    return (
      <main className={styles.page}>
        <PageHeader title="Loading…" subtitle={slug} compact />
        <div className={styles.loading}>Fetching this game’s photos…</div>
      </main>
    );
  }

  if (state.status === 'missing') {
    return (
      <main className="page">
        <PageHeader title="Game not found" subtitle={slug} />
        <Placeholder
          phase="Not found"
          what={`No game lives at /play/${slug}`}
          detail="It may never have finished saving. Head back and make your own."
        />
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="page">
        <PageHeader title="Something went wrong" subtitle={slug} />
        <Placeholder phase="Error" what={state.message} detail="Try reloading the page." />
      </main>
    );
  }

  const { game, art } = state;
  const canSubmit = finalScore != null && finalScore > 0 && scores.mine == null;

  return (
    <main className={styles.page}>
      <PageHeader
        title={game.title || 'MergeBalls'}
        // The way back to the roster, kept as quiet as it can be while still being
        // findable: whoever made this game will look here, and nobody else needs to.
        subtitle={
          <>
            /play/{slug} ·{' '}
            <Link className={styles.editLink} to={`/edit/${slug}`}>
              edit photos
            </Link>
          </>
        }
        compact
      />

      <GameBoard
        ballCount={game.balls.length}
        art={art}
        bestKey={slug}
        onGameOver={handleGameOver}
        onRestart={handleRestart}
        shareUrl={`${window.location.origin}/play/${slug}`}
        shareTitle={game.title || undefined}
        overlayExtra={
          canSubmit ? (
            <ScoreSubmit
              name={scores.name}
              onNameChange={scores.setName}
              onSubmit={() => void scores.submit(finalScore)}
              submitting={scores.submitting}
              error={scores.error}
            />
          ) : (
            <Leaderboard scores={scores.scores} mine={scores.mine} limit={3} />
          )
        }
        footer={
          <ScoreStrip
            scores={scores.scores}
            onOpen={() => setBoardOpen(true)}
            shareUrl={`${window.location.origin}/play/${slug}`}
          />
        }
      />

      {boardOpen ? (
        <Sheet title="Top scores" onClose={() => setBoardOpen(false)}>
          <Leaderboard scores={scores.scores} mine={scores.mine} />
        </Sheet>
      ) : null}
    </main>
  );
}
